/* eslint-disable max-len */
import { useContext, useEffect, useState } from 'react';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useQueryAttributes } from './useQueryAttributes';
import { getQueryArgs, getQueryFromAttributes, getQueryType } from './getQueryFromAttributes';
import { useQuery } from '../../hooks/useQuery';
import QueryContext from '../QueryContext';
import ServerContext from '../ServerContext';

const defaultOpts = {
  ssr: true,
  errorPolicy: 'all'
};

// these two function will move to experience as options on DataProvider
const useIsMounted = () => {
  const storeId = useStoreId();
  return storeId !== '8119';
};

const shouldSkip = ({ options, mounted }) => {
  // skip renders until
  if (options.variables?.storeId && options.ssr === false && !mounted) {
    // eslint-disable-next-line no-param-reassign
    return true;
  }

  if (options.variables?.storeId === '8119' && mounted) {
    return true;
  }

  return false;
};

const defaultMountFn = ({ mounted }) => mounted;
const defaultSkipFn = ({ skip }) => skip;
const defaultOptionsFn = ({ options }) => options;

export const useDataModel = (queryName, opts = defaultOpts, lazy = false) => {
  const options = {
    ...defaultOpts,
    ...opts,
  };

  const { batchLinkTimeout, useBatchLink } = useContext(ServerContext);

  const {
    dataSource,
    defaultVariables,
    queryAttributes = {},
    mounted: mountedFn = defaultMountFn,
    skip: skipFn = defaultSkipFn,
    queryOptions: optionsFn = defaultOptionsFn,
    merge
  } = useContext(QueryContext);

  // method to wait for client render
  const mounted = mountedFn({
    mounted: typeof options.mounted !== 'undefined'
      ? options.mounted
      : useIsMounted()
  });

  const variables = options.variables
    ? { ...options.variables }
    : {};

  const ssr = options.ssr && !mounted;

  if (merge) {
    // eslint-disable-next-line no-param-reassign
    const keys = Object.keys(merge);
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const ret = merge[key].fn({ query: queryName, ssr: options.ssr, mounted });
      if (ret !== queryName) {
        // eslint-disable-next-line no-param-reassign
        queryName = ret;
        break;
      }
    }
  }
  const { attributes, queryName: aliasedQueryName } = useQueryAttributes({ query: queryName, ssr, dataModel: opts.dataModel });
  options.skip = skipFn({ skip: options.skip || shouldSkip({ options, mounted }), queryName, attributes });
  const isomorphicQueryType = ssr
    ? 'server'
    : 'client';

  if (!queryAttributes[isomorphicQueryType]) {
    queryAttributes[isomorphicQueryType] = {};
  }

  if (!queryAttributes[isomorphicQueryType][queryName]) {
    queryAttributes[isomorphicQueryType][queryName] = {};
  }
  let query;
  if (queryAttributes[isomorphicQueryType][queryName]._cachedQuery && !opts.dataModel) {
    query = queryAttributes[isomorphicQueryType][queryName]._cachedQuery;
  } else {
    query = getQueryFromAttributes({ name: queryName, attributes, ssr: options.ssr && !mounted });
    queryAttributes[isomorphicQueryType][queryName]._cachedQuery = query;
  }

  let args;
  if (queryAttributes[isomorphicQueryType][queryName]._cachedArgs && !opts.dataModel) {
    args = queryAttributes[isomorphicQueryType][queryName]._cachedArgs;
  } else {
    args = getQueryArgs({ attributes, ssr, asString: false });
    queryAttributes[isomorphicQueryType][queryName]._cachedArgs = args;
  }

  const queryType = getQueryType({ attributes, name: queryName });

  // if args contain more than all variables
  const missingVariables = Object.keys(args || {}).filter((key) => {
    return !variables?.[key];
  });
  if (missingVariables.length) {
    missingVariables.forEach((key) => {
      if (dataSource && key === 'dataSource') {
        variables.dataSource = dataSource;
      }
      if (typeof defaultVariables.current[key] !== 'undefined') {
        variables[key] = defaultVariables.current[key];
      } else if (args[key]._isRequired && queryType !== 'mutation' && !lazy && !options.skip) {
        throw new Error(`'${key}' is missing from options.variables in useDataModel hook.
This likely due to one of the following reasons:
  1. A parameter was included when building the component dataModel but not passed into options.variables.
  2. The component dataModel was merged with another component requesting the '${key}' using params(). Implement defaultVaribles.${key} attribute in the QueryProvider to avoid this error.`);
      }
    });
  }

  // sort variables alphabetically for matching queries
  const sortedKeys = Object.keys(variables).sort();

  options.variables = sortedKeys.reduce((acc, cur) => {
    return {
      ...acc,
      [cur]: variables[cur]
    };
  }, {});

  // @todo remove this business logic, should be controlled by queryprovider
  if (!options.fetchPolicy && dataSource && dataSource !== 'catalog') {
    options.fetchPolicy = 'cache-only';
  }

  const cachedVariables = {
    ...(options.variables || {})
  };

  const optionsForQuery = optionsFn({ options, queryName });

  if (queryType === 'mutation') {
    return useMutation(query, optionsForQuery);
  }

  if (lazy) {
    const [exec, resp] = useLazyQuery(query, optionsForQuery);
    if (resp.data) {
      if (ssr) {
        queryAttributes.server[queryName]._isResolved = true;
      } else if (!resp.loading) {
        queryAttributes.client[queryName]._isResolved = true;
      }
    }
    return [exec, resp, cachedVariables];
  }

  if (useBatchLink && batchLinkTimeout) {
    optionsForQuery.context = {
      ...(optionsForQuery.context || {}),
      timeout: batchLinkTimeout
    };
  }

  const response = useQuery(query, optionsForQuery);

  if (response.data) {
    if (ssr) {
      queryAttributes.server[queryName]._isResolved = true;
    } else if (!response.loading) {
      queryAttributes.client[queryName]._isResolved = true;
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS && response.data && !response.loading) {
      const { data, variables: responseVariables } = response;

      window.LIFE_CYCLE_EVENT_BUS.trigger('data-sources.data', {
        type: 'product',
        queryName,
        variables: responseVariables,
        data
      });
    }
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS && response.error && !response.loading) {
      const { data, error, variables: responseVariables } = response;

      window.LIFE_CYCLE_EVENT_BUS.trigger('data-sources.error', {
        type: 'product',
        queryName,
        variables: responseVariables,
        data,
        message: `GQL Error: ${queryName} - ${error?.message}`
      });
    }
  }, [response.loading]);

  return response;
};
